import React, { FC } from "react";

import classNames from "classnames";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useCreateUserSource } from "@api";

import { usePartnerInfo } from "@utils";

import { Modal } from "@ui/components";
import { Button, Icon, Text, Title } from "@ui/elements";
import { Input, Radio } from "@ui/forms";
import { Stack } from "@ui/layout";

import { CommonModalProps, toaster } from "@components";

import { getUtmParams } from "../../utils/getUtmParams";

type UserSourceModalFormFields = {
  source: string;
  otherValue: string;
};

export const UserSourceModal: FC<CommonModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const form = useForm<UserSourceModalFormFields>({
    defaultValues: {
      otherValue: "",
    },
  });
  const { mutateAsync: createUserSource, isPending: createSourceLoading } =
    useCreateUserSource();
  const source = form.watch("source");
  const partnerInfo = usePartnerInfo();

  const onSubmit = async (values: UserSourceModalFormFields) => {
    const utm_source = getUtmParams().utm_source;
    if (values.source === "other") {
      await createUserSource({
        data: {
          source: `other - ${values.otherValue}`,
          utm_source,
          referrer: localStorage.getItem("user_source") || "",
        },
      });
    } else {
      await createUserSource({
        data: {
          source: values.source,
          utm_source,
          referrer: localStorage.getItem("user_source") || "",
        },
      });
    }

    onClose();

    toaster.success({
      title: t("userSourceModal.toaster.title"),
      message: t("userSourceModal.toaster.message"),
    });
  };

  return (
    <Modal
      disableClose={true}
      header={
        <Stack>
          <Icon icon="Sparkle" weight="fill" />
          <Title size={4}>
            {t("userSourceModal.title", { partner: partnerInfo.name })}
          </Title>
        </Stack>
      }
      isActive={isOpen}
      onClose={onClose}
      footer={
        <Stack justify="end" isFullwidth>
          <Button
            type="submit"
            color="primary"
            onClick={form.handleSubmit(onSubmit)}
            isLoading={createSourceLoading}
          >
            {t("userSourceModal.continue")}
          </Button>
        </Stack>
      }
    >
      <Stack direction="column" gap="xl">
        <Stack direction="column">
          <Text weight="bold">{t("userSourceModal.subtitle")}</Text>
          <Text>
            {t("userSourceModal.description", { partner: partnerInfo.name })}
          </Text>
        </Stack>

        <Stack direction="column" gap="sm">
          <Radio
            {...form.register("source", {
              required: t("userSourceModal.errors.selectSource"),
            })}
            value="instagram"
          >
            {t("userSourceModal.options.instagram")}
          </Radio>
          <Radio
            {...form.register("source", {
              required: t("userSourceModal.errors.selectSource"),
            })}
            value="facebook"
          >
            {t("userSourceModal.options.facebook")}
          </Radio>
          <Radio
            {...form.register("source", {
              required: t("userSourceModal.errors.selectSource"),
            })}
            value="tiktok"
          >
            {t("userSourceModal.options.tiktok")}
          </Radio>
          <Radio
            {...form.register("source", {
              required: t("userSourceModal.errors.selectSource"),
            })}
            value="google"
          >
            {t("userSourceModal.options.google")}
          </Radio>
          <Radio
            {...form.register("source", {
              required: t("userSourceModal.errors.selectSource"),
            })}
            value="referral"
          >
            {t("userSourceModal.options.referral")}
          </Radio>
          <Radio
            {...form.register("source", {
              required: t("userSourceModal.errors.selectSource"),
            })}
            value="other"
          >
            {t("userSourceModal.options.other")}
          </Radio>
          {form.formState.errors.source && (
            <p className="help is-danger">
              {form.formState.errors.source.message}
            </p>
          )}
          <div className={classNames({ "is-hidden": source !== "other" })}>
            <Input
              {...form.register("otherValue", {
                required:
                  source === "other"
                    ? t("userSourceModal.errors.specifySource")
                    : false,
              })}
              error={form.formState.errors.otherValue?.message}
              placeholder={t("userSourceModal.inputPlaceholder")}
            />
          </div>
        </Stack>
      </Stack>
    </Modal>
  );
};
